import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  ChevronLeft, ChevronRight, Users, AlertTriangle, Loader2,
  CalendarDays, Search, X, CheckCircle2, XCircle, MessageSquare, Clock,
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { getTeamCalendar, getTeamSummary, leaveRequestApproval, getLeaveRequest, getLeaveTypes, approveLeave, rejectLeave } from '../../../api/Attendance/leaves';
import { TeamCalendarView, CalendarEntry, TeamSummaryView, TeamMemberInfo, TeamDepartmentInfo, LeaveRequestView, LeaveTypeView } from '../../../models/attendance';
import { getFullUrl } from '../../../utils/url';
import { extractApiError } from '../../../utils/errors';
import { CoverageIndicator } from '../../../components/leaves/CoverageIndicator';
import PageShell from '../../../components/leaves/PageShell';
import RequestDetailModal from '../LeaveApprovals/components/RequestDetailModal';
import Pagination from '@/components/Pagination';

type ViewMode = 'week' | 'month';

interface TeamCalendarProps {
  userId: string;
  company: { id: number };
}

// --- Helpers ---

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - day + 1); // Monday
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDateParam(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Default non-working days (Friday + Saturday) used when API hasn't loaded yet
const DEFAULT_NON_WORKING_DAYS = [5, 6];

// Leave type to color mapping — supports both named colors and hex codes
function getLeaveColor(leaveTypeColor?: string): {
  bg: string; text: string; border: string;
  bgStyle?: React.CSSProperties;
} {
  const color = (leaveTypeColor || '').trim();

  // Handle hex codes via inline styles — use black text for contrast
  if (color.startsWith('#')) {
    return {
      bg: '', text: 'text-black', border: '',
      bgStyle: { backgroundColor: `${color}30` },
    };
  }

  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    green: { bg: 'bg-emerald-200', text: 'text-black', border: 'border-emerald-400' },
    blue: { bg: 'bg-blue-200', text: 'text-black', border: 'border-blue-400' },
    red: { bg: 'bg-red-200', text: 'text-black', border: 'border-red-400' },
    orange: { bg: 'bg-orange-200', text: 'text-black', border: 'border-orange-400' },
    purple: { bg: 'bg-purple-200', text: 'text-black', border: 'border-purple-400' },
    pink: { bg: 'bg-pink-200', text: 'text-black', border: 'border-pink-400' },
    gray: { bg: 'bg-gray-200', text: 'text-black', border: 'border-border' },
  };
  const key = (color || 'blue').toLowerCase();
  return colorMap[key] || colorMap.blue;
}

// --- Component ---

const TeamCalendar: React.FC<TeamCalendarProps> = () => {
  const { t } = useTranslation(['leaves', 'common']);

  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(() => startOfWeek(new Date()));
  const [calendarData, setCalendarData] = useState<TeamCalendarView | null>(null);
  const [teamSummary, setTeamSummary] = useState<TeamSummaryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Leave cell popover state
  const [approvalLoading, setApprovalLoading] = useState<number | null>(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [showCommentField, setShowCommentField] = useState(false);

  // Detail modal state
  const [detailRequest, setDetailRequest] = useState<LeaveRequestView | null>(null);
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);

  // Search, filter, pagination state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<number | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 25;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPageNumber(1);
    }, 300);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPageNumber(1);
  };

  // Compute date range
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      return { from: start, to: addDays(start, 6) };
    }
    const start = startOfMonth(currentDate);
    return { from: start, to: endOfMonth(currentDate) };
  }, [currentDate, viewMode]);

  // Generate array of days for display
  const days = useMemo(() => {
    const result: Date[] = [];
    let d = new Date(dateRange.from);
    while (d <= dateRange.to) {
      result.push(new Date(d));
      d = addDays(d, 1);
    }
    return result;
  }, [dateRange]);

  // Pre-parse entry dates once and group by userId for O(1) lookup
  type ParsedEntry = CalendarEntry & { _start: number; _end: number };
  const memberEntries = useMemo(() => {
    if (!calendarData) return new Map<string, ParsedEntry[]>();

    const map = new Map<string, ParsedEntry[]>();
    for (const entry of calendarData.entries) {
      const s = new Date(entry.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(entry.endDate); e.setHours(0, 0, 0, 0);
      const parsed: ParsedEntry = { ...entry, _start: s.getTime(), _end: e.getTime() };
      if (!map.has(entry.userId)) {
        map.set(entry.userId, []);
      }
      map.get(entry.userId)!.push(parsed);
    }
    return map;
  }, [calendarData]);

  // Full team roster from API (already sorted by name on backend)
  const members = useMemo((): (TeamMemberInfo & { entries: ParsedEntry[] })[] => {
    if (!calendarData?.members?.length) {
      // Fallback: derive from entries for backward compatibility
      const seen = new Map<string, TeamMemberInfo & { entries: ParsedEntry[] }>();
      for (const [userId, entries] of memberEntries) {
        const first = entries[0];
        if (first && !seen.has(userId)) {
          seen.set(userId, { userId, userName: first.userName, imageUrl: first.imageUrl, entries });
        }
      }
      return Array.from(seen.values()).sort((a, b) => a.userName.localeCompare(b.userName));
    }
    return calendarData.members.map(m => ({
      ...m,
      entries: memberEntries.get(m.userId) || [],
    }));
  }, [calendarData, memberEntries]);

  // Daily off counts for coverage (uses pre-parsed timestamps for fast comparison)
  const dailyOffCounts = useMemo(() => {
    const counts: Map<string, number> = new Map();
    for (const day of days) {
      const dateKey = formatDateParam(day);
      const dayTime = day.getTime();
      let count = 0;
      for (const member of members) {
        const isOff = member.entries.some(e =>
          dayTime >= e._start && dayTime <= e._end && e.status === 'Approved'
        );
        if (isOff) count++;
      }
      counts.set(dateKey, count);
    }
    return counts;
  }, [days, members]);

  // Use totalMembers from API response for accurate coverage across all members
  const totalMembers = useMemo(() => {
    return calendarData?.totalMembers || Math.max(members.length, teamSummary?.totalTeamMembers || 0, 1);
  }, [calendarData, teamSummary, members]);

  const totalPages = calendarData?.totalPages || 1;
  const departments: TeamDepartmentInfo[] = calendarData?.departments || [];

  // Non-working days from API (derived from team work schedules)
  const nonWorkingDaysSet = useMemo(() => {
    const days = calendarData?.nonWorkingDays ?? DEFAULT_NON_WORKING_DAYS;
    return new Set(days);
  }, [calendarData?.nonWorkingDays]);

  const isNonWorkingDay = useCallback((date: Date): boolean => {
    return nonWorkingDaysSet.has(date.getDay());
  }, [nonWorkingDaysSet]);

  // Unique leave types from current entries for dynamic legend
  const uniqueLeaveTypes = useMemo(() => {
    if (!calendarData?.entries?.length) return [];
    const seen = new Map<string, string | undefined>();
    for (const entry of calendarData.entries) {
      if (!seen.has(entry.leaveType)) {
        seen.set(entry.leaveType, entry.leaveTypeColor);
      }
    }
    return Array.from(seen.entries())
      .map(([name, color]) => ({ name, color }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [calendarData?.entries]);

  // Fetch summary independently (only on mount and after approval actions)
  const fetchSummary = useCallback(async () => {
    try {
      const summaryRes = await getTeamSummary();
      if (summaryRes.errors.length === 0) {
        setTeamSummary(summaryRes.payload);
      }
    } catch { /* summary failure is non-blocking */ }
  }, []);

  useEffect(() => {
    fetchSummary();
    getLeaveTypes().then(res => {
      if (res.errors.length === 0) setLeaveTypes(res.payload);
    }).catch(() => {});
  }, [fetchSummary]);

  // Open the detail modal for a leave request
  const handleViewDetails = async (leaveRequestId: number) => {
    setDetailLoading(leaveRequestId);
    try {
      const res = await getLeaveRequest(leaveRequestId);
      if (res.errors.length === 0) {
        setDetailRequest(res.payload);
      }
    } catch { /* ignore */ }
    setDetailLoading(null);
  };

  const handleModalApprove = async (requestId: number, comments: string) => {
    const res = await approveLeave(requestId, comments);
    if (res.errors.length > 0) throw new Error(res.errors[0]?.message);
    fetchData();
    fetchSummary();
  };

  const handleModalReject = async (requestId: number, comments: string) => {
    const res = await rejectLeave(requestId, comments);
    if (res.errors.length > 0) throw new Error(res.errors[0]?.message);
    fetchData();
    fetchSummary();
  };

  // Fetch calendar data (depends on date range, search, filter, page)
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const calRes = await getTeamCalendar(formatDateParam(dateRange.from), formatDateParam(dateRange.to), {
        search: searchQuery || undefined,
        departmentId: selectedDepartment,
        pageNumber,
        pageSize,
      });
      if (calRes.errors.length === 0) {
        setCalendarData(calRes.payload);
      }
    } catch {
      setError(t('Error loading data'));
    }
    setLoading(false);
  }, [dateRange, searchQuery, selectedDepartment, pageNumber, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setPageNumber(1);
  }, [selectedDepartment]);

  // Navigation
  const navigatePrev = () => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      const d = new Date(currentDate);
      d.setMonth(d.getMonth() - 1);
      setCurrentDate(d);
    }
  };

  const navigateNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      const d = new Date(currentDate);
      d.setMonth(d.getMonth() + 1);
      setCurrentDate(d);
    }
  };

  const goToToday = () => {
    if (viewMode === 'month') {
      setCurrentDate(startOfMonth(new Date()));
    } else {
      setCurrentDate(startOfWeek(new Date()));
    }
  };

  // Handle approve/reject from popover
  const handleApproval = async (leaveRequestId: number, isApproved: boolean) => {
    if (!isApproved && !approvalComment.trim()) {
      setShowCommentField(true);
      return;
    }
    setApprovalLoading(leaveRequestId);
    try {
      const res = await leaveRequestApproval({
        id: leaveRequestId,
        isApproved,
        managerComment: approvalComment.trim() || undefined,
      });
      if (res.errors.length === 0) {
        setApprovalComment('');
        setShowCommentField(false);
        fetchData();
        fetchSummary();
      }
    } catch (err) {
      const msg = extractApiError(err, t('Something went wrong'));
      setError(msg);
    }
    setApprovalLoading(null);
  };

  const resetPopoverState = () => {
    setApprovalComment('');
    setShowCommentField(false);
  };

  // Calculate leave duration in days
  const calcLeaveDays = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  // Check if a member is off on a given day (uses pre-parsed timestamps)
  const getMemberLeaveForDay = (entries: ParsedEntry[], day: Date): ParsedEntry | undefined => {
    const dayTime = day.getTime();
    return entries.find(e => dayTime >= e._start && dayTime <= e._end);
  };

  // Format header
  const headerLabel = useMemo(() => {
    if (viewMode === 'week') {
      const start = dateRange.from;
      const end = dateRange.to;
      const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, { ...opts, year: 'numeric' })}`;
    }
    return dateRange.from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [dateRange, viewMode]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-foreground mb-4">{error}</p>
          <button
            onClick={fetchData}
            data-testid="leave-teamcal-retry"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={t('Team Calendar')}
      description={t('teamCalendarDescription')}
      icon={CalendarDays}
      accentColor="emerald"
      maxWidth="1600px"
      statsColumns={3}
      stats={[
        { label: t('teamMembersCount', { count: totalMembers }).replace(`${totalMembers} `, ''), value: totalMembers, color: 'text-teal-600' },
        { label: t('offToday', { count: teamSummary?.onLeaveToday || 0 }).replace(`${teamSummary?.onLeaveToday || 0} `, ''), value: teamSummary?.onLeaveToday || 0, color: 'text-orange-600' },
        { label: t('Coverage'), value: `${teamSummary?.coveragePercentage || 100}%`, color: 'text-blue-600' },
      ]}
      className="min-h-screen bg-background"
    >
      {/* Controls */}
      <div className="py-4">
        <div className="flex flex-col gap-4">
          {/* Row 1: Navigation + View Mode */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={navigatePrev}
                data-testid="calendar-prev"
                className="p-2 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronLeft size={18} className="rtl:rotate-180" />
              </button>
              <button
                onClick={goToToday}
                data-testid="calendar-today"
                className="px-3 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
              >
                {t('Today')}
              </button>
              <button
                onClick={navigateNext}
                data-testid="calendar-next"
                className="p-2 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronRight size={18} className="rtl:rotate-180" />
              </button>
              <span className="ltr:ml-2 rtl:mr-2 text-lg font-semibold text-foreground">{headerLabel}</span>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
              <button
                onClick={() => { setViewMode('week'); setCurrentDate(startOfWeek(currentDate)); }}
                data-testid="view-week"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'week' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('View Week')}
              </button>
              <button
                onClick={() => { setViewMode('month'); setCurrentDate(startOfMonth(currentDate)); }}
                data-testid="view-month"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'month' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('View Month')}
              </button>
            </div>
          </div>

          {/* Row 2: Search + Department Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('teamCalendarSearchPlaceholder')}
                data-testid="calendar-search"
                className="w-full ltr:pl-9 rtl:pr-9 ltr:pr-8 rtl:pl-8 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
              />
              {searchInput && (
                <button
                  onClick={clearSearch}
                  data-testid="calendar-search-clear"
                  className="absolute ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Department Filter */}
            {departments.length > 0 && (
              <select
                value={selectedDepartment ?? ''}
                onChange={(e) => setSelectedDepartment(e.target.value ? Number(e.target.value) : undefined)}
                data-testid="calendar-department-filter"
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 min-w-[180px]"
              >
                <option value="">{t('allDepartments')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="pb-8">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 size={32} className="animate-spin text-muted-foreground" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-24">
              <Users size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('No team data')}</h3>
              <p className="text-muted-foreground">
                {searchQuery || selectedDepartment ? t('teamCalendarNoFilterResults') : t('noTeamDataMessage')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="team-calendar-grid">
                {/* Column Headers: Days */}
                <thead>
                  <tr>
                    <th className="sticky ltr:left-0 rtl:right-0 z-10 bg-card border-b ltr:border-r rtl:border-l border-border px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[180px]">
                      <div className="flex items-center gap-1">
                        <Users size={14} />
                        {t('teamMembersCount', { count: totalMembers })}
                      </div>
                    </th>
                    {days.map((day) => {
                      const dateKey = formatDateParam(day);
                      const offCount = dailyOffCounts.get(dateKey) || 0;
                      const isToday = isSameDay(day, today);
                      const nonWorkingDay = isNonWorkingDay(day);

                      return (
                        <th
                          key={dateKey}
                          className={`border-b border-r border-border px-1 py-2 text-center min-w-[48px] ${
                            nonWorkingDay ? 'bg-muted/30' : isToday ? 'bg-blue-50/60' : ''
                          }`}
                        >
                          <div className={`text-[10px] font-medium uppercase ${isToday ? 'text-blue-600' : 'text-muted-foreground'}`}>
                            {day.toLocaleDateString(undefined, { weekday: 'short' })}
                          </div>
                          <div className={`text-sm font-semibold leading-6 ${
                            isToday
                              ? 'bg-blue-600 text-white w-6 h-6 rounded-full mx-auto flex items-center justify-center'
                              : 'text-foreground'
                          }`}>
                            {day.getDate()}
                          </div>
                          {/* Coverage indicator */}
                          <CoverageIndicator
                            totalMembers={totalMembers}
                            offCount={offCount}
                            variant="badge"
                            className="mx-auto mt-1"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {/* Rows: Team Members */}
                <tbody>
                  {members.map((member) => (
                    <tr key={member.userId} className="hover:bg-muted/20" data-testid="calendar-member-row">
                      {/* Member Info (sticky left) */}
                      <td className="sticky ltr:left-0 rtl:right-0 z-10 bg-card border-b ltr:border-r rtl:border-l border-border px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-medium overflow-hidden">
                            {getFullUrl(member.imageUrl) ? (
                              <img src={getFullUrl(member.imageUrl)!} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (member.userName || '?').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {member.userName}
                            </div>
                            {member.departmentName && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {member.departmentName}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Day Cells */}
                      {days.map((day) => {
                        const dateKey = formatDateParam(day);
                        const leave = getMemberLeaveForDay(member.entries, day);
                        const nonWorkingDay = isNonWorkingDay(day);
                        const isToday = isSameDay(day, today);

                        if (leave) {
                          const colors = getLeaveColor(leave.leaveTypeColor);
                          const isPending = leave.status === 'Pending';
                          const leaveDays = calcLeaveDays(leave.startDate, leave.endDate);
                          const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

                          return (
                            <td
                              key={dateKey}
                              className={`border-b border-r border-border px-0 py-0 ${
                                isToday ? 'bg-blue-50/60' : ''
                              }`}
                            >
                              <Popover.Root onOpenChange={(open) => { if (!open) resetPopoverState(); }}>
                                <Popover.Trigger asChild>
                                  <button
                                    type="button"
                                    data-testid="leave-cell-trigger"
                                    className={`w-full h-full min-h-[36px] ${colors.bg} ${isPending ? 'opacity-60' : ''} flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-inset hover:ring-teal-500 transition-shadow focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500`}
                                    style={colors.bgStyle}
                                  >
                                    <span className={`text-[10px] font-medium ${colors.text} truncate px-1`}>
                                      {leave.leaveType.substring(0, 3)}
                                    </span>
                                  </button>
                                </Popover.Trigger>
                                <Popover.Portal>
                                  <Popover.Content
                                    data-testid="leave-cell-popover"
                                    side="bottom"
                                    align="center"
                                    sideOffset={4}
                                    className="z-50 w-72 bg-card border border-border rounded-xl shadow-xl animate-in fade-in-0 zoom-in-95"
                                  >
                                    {/* Popover Header */}
                                    <div className={`px-4 py-3 border-b border-border rounded-t-xl ${colors.bg}`} style={colors.bgStyle}>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center flex-shrink-0 text-white text-[10px] font-medium overflow-hidden">
                                            {getFullUrl(member.imageUrl) ? (
                                              <img src={getFullUrl(member.imageUrl)!} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              (member.userName || '?').charAt(0).toUpperCase()
                                            )}
                                          </div>
                                          <div className="min-w-0">
                                            <div className={`text-sm font-semibold ${colors.text} truncate`}>{member.userName}</div>
                                            {member.departmentName && (
                                              <div className="text-[10px] text-muted-foreground truncate">{member.departmentName}</div>
                                            )}
                                          </div>
                                        </div>
                                        <Popover.Close
                                          data-testid="leave-cell-popover-close"
                                          className="p-1 rounded hover:bg-black/10 text-muted-foreground"
                                        >
                                          <X size={14} />
                                        </Popover.Close>
                                      </div>
                                    </div>

                                    {/* Popover Body */}
                                    <div className="px-4 py-3 space-y-2.5">
                                      {/* Leave Type */}
                                      <div className="flex items-center gap-2">
                                        <CalendarDays size={14} className="text-muted-foreground flex-shrink-0" />
                                        <span className="text-sm font-medium text-foreground">{leave.leaveType}</span>
                                      </div>

                                      {/* Date Range */}
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Clock size={14} className="flex-shrink-0" />
                                        <span>
                                          {new Date(leave.startDate).toLocaleDateString(undefined, dateOpts)}
                                          {' — '}
                                          {new Date(leave.endDate).toLocaleDateString(undefined, dateOpts)}
                                        </span>
                                      </div>

                                      {/* Duration + Status */}
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">
                                          {t('calendarPopoverDuration', { days: leaveDays })}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                                          isPending
                                            ? 'bg-amber-100 text-black'
                                            : leave.status === 'Approved'
                                            ? 'bg-emerald-100 text-black'
                                            : leave.status === 'Rejected'
                                            ? 'bg-red-100 text-black'
                                            : 'bg-gray-100 text-black'
                                        }`}>
                                          {isPending && <Clock size={10} />}
                                          {leave.status === 'Approved' && <CheckCircle2 size={10} />}
                                          {leave.status === 'Rejected' && <XCircle size={10} />}
                                          {t(leave.status)}
                                        </span>
                                      </div>
                                    </div>

                                    {/* View Details */}
                                    <div className="px-4 pb-1">
                                      <Popover.Close asChild>
                                        <button
                                          onClick={() => handleViewDetails(leave.leaveRequestId)}
                                          disabled={detailLoading === leave.leaveRequestId}
                                          data-testid="leave-cell-view-details"
                                          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                          {detailLoading === leave.leaveRequestId ? (
                                            <Loader2 size={14} className="animate-spin" />
                                          ) : (
                                            <CalendarDays size={14} />
                                          )}
                                          {t('View Details')}
                                        </button>
                                      </Popover.Close>
                                    </div>

                                    {/* Approve/Reject Actions for Pending */}
                                    {isPending && (
                                      <div className="px-4 py-3 border-t border-border space-y-2">
                                        {showCommentField && (
                                          <div className="relative">
                                            <MessageSquare size={14} className="absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-muted-foreground" />
                                            <textarea
                                              value={approvalComment}
                                              onChange={(e) => setApprovalComment(e.target.value)}
                                              placeholder={t('calendarPopoverCommentPlaceholder')}
                                              data-testid="leave-cell-comment"
                                              rows={2}
                                              className="w-full ltr:pl-8 rtl:pr-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                            />
                                            <p className="text-[10px] text-muted-foreground ltr:ml-1 rtl:mr-1 mt-0.5">
                                              {t('commentRequiredForRejection')}
                                            </p>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2 rtl:space-x-reverse">
                                          <button
                                            onClick={() => handleApproval(leave.leaveRequestId, true)}
                                            disabled={approvalLoading === leave.leaveRequestId}
                                            data-testid="leave-cell-approve"
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                          >
                                            {approvalLoading === leave.leaveRequestId ? (
                                              <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                              <CheckCircle2 size={14} />
                                            )}
                                            {t('Approve')}
                                          </button>
                                          <button
                                            onClick={() => handleApproval(leave.leaveRequestId, false)}
                                            disabled={approvalLoading === leave.leaveRequestId}
                                            data-testid="leave-cell-reject"
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                                          >
                                            {approvalLoading === leave.leaveRequestId ? (
                                              <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                              <XCircle size={14} />
                                            )}
                                            {t('Reject')}
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    <Popover.Arrow className="fill-border" />
                                  </Popover.Content>
                                </Popover.Portal>
                              </Popover.Root>
                            </td>
                          );
                        }

                        return (
                          <td
                            key={dateKey}
                            className={`border-b border-r border-border ${
                              nonWorkingDay ? 'bg-muted/20' : isToday ? 'bg-blue-50/60' : ''
                            }`}
                          >
                            <div className="min-h-[36px]" />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
            <Pagination
                currentPage={pageNumber}
                totalPages={totalPages}
                onPageChange={(p) => setPageNumber(p)}
                variant="full"
                showEdges={false}
                totalCount={totalMembers}
            />
        )}

        {/* Legend */}
        {members.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="calendar-legend">
            {/* Coverage legend */}
            <span className="font-medium">{t('Coverage')}:</span>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" />
              <span>{t('Critical')} (&lt;40%)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-yellow-100 border border-yellow-300" />
              <span>{t('Low')} (&lt;70%)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-300" />
              <span>{t('OK')}</span>
            </div>

            {/* Dynamic leave type legend with status indicators */}
            {uniqueLeaveTypes.length > 0 && (
              <>
                <span className="ltr:ml-4 rtl:mr-4 font-medium">{t('Leave Types')}:</span>
                {uniqueLeaveTypes.map(lt => {
                  const colors = getLeaveColor(lt.color);
                  return (
                    <div key={lt.name} className="flex items-center gap-1" data-testid="legend-leave-type">
                      <span
                        className={`inline-block w-3 h-3 rounded ${colors.bg} ${colors.border ? `border ${colors.border}` : ''}`}
                        style={colors.bgStyle}
                      />
                      <span>{lt.name}</span>
                    </div>
                  );
                })}
              </>
            )}

            {/* Status legend */}
            <span className="ltr:ml-4 rtl:mr-4 font-medium">{t('Status')}:</span>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-gray-300" />
              <span>{t('Approved')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-gray-300 opacity-60" />
              <span>{t('Pending')} ({t('faded')})</span>
            </div>
          </div>
        )}
      </div>

      {/* Request Detail Modal */}
      {detailRequest && (
        <RequestDetailModal
          request={detailRequest}
          leaveTypes={leaveTypes}
          onClose={() => setDetailRequest(null)}
          onApprove={handleModalApprove}
          onReject={handleModalReject}
        />
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  company: state.user.company,
});

export default connect(mapStateToProps)(TeamCalendar);
